import { type SystemStyleObject } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';

import type { InputState } from '@burnsred/entity-form';

import {
  Checkbox as ChakraCheckbox,
  type CheckboxProps as ChakraCheckboxProps,
} from '@/components/chakra';
import { filterProps, isFieldRequired } from '@/forms/utils';

export type CheckboxProps = InputState &
  Omit<ChakraCheckboxProps, 'onChange' | 'children'> & {
    /** optional; passed as `children` to ChakraCheckbox */
    label?: string;
    /**
     * Optional function to modify evaluation of isChecked.
     *By default, isChecked is: `!!value`.
     */
    computeIsChecked?: (value: unknown) => boolean;
  };

/**
 * Renders a single connected Chakra Checkbox
 */
export const Checkbox = (props: CheckboxProps) => {
  const {
    css = widgetSingleCheckboxCss,
    computeIsChecked,
    errors,
    field,
    label,
    name,
    onChange,
    value,
    ...restProps
  } = props;

  const handleChange = useCallback(() => {
    onChange({
      target: {
        name,
        value: !value,
      },
    });
  }, [name, onChange, value]);

  const isChecked = useMemo(
    () => computeIsChecked?.(value) ?? !!value,
    [computeIsChecked, value],
  );

  /** allow overriding via props, or defaulting to the field's blank state */
  const required =
    typeof props.required !== 'undefined'
      ? props.required
      : isFieldRequired(field);

  return (
    <ChakraCheckbox
      css={css}
      data-field-name={props.name}
      checked={isChecked}
      onCheckedChange={handleChange}
      required={required}
      invalid={!!errors?.size || undefined}
      {...filterProps(restProps)}
    >
      {label}
    </ChakraCheckbox>
  );
};

/** if overriding this in your component, you may want to preserve the checkbox alignment */
export const widgetSingleCheckboxCss: SystemStyleObject = {
  // &.chakra-field__root
  '&': {
    '& .chakra-checkbox__root': {
      // alignItems: 'center',
    },
  },
};
